'use client';

import cn from 'classnames';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { responsiveBreakpointsPx } from '@/constants/responsiveness';
import styles from './styles/select.module.scss';
import type { SelectProps, State } from './interfaces';
import { SelectContext } from './SelectContext';
import { useSelectContextUtils } from './useOptionElementUtils';
import { InvalidFeedback } from '../InvalidFeedback/InvalidFeedback';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useClickOutside from '@/hooks/useClickOutside';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { VALIDITY } from '@/constants/validity';
import ucfirst from '@/packages/string/ucfirst';
import { IconDropDown } from '@/assets/icons/IconDropdown';
import { CloseIcon } from '@/assets/icons/CloseIcon';

const CLICK_OUTSIDE_STATE = {};

export function Select<ObjectType>(props: SelectProps<ObjectType>): React.JSX.Element {
    const [state, setState] = useState<State<ObjectType>>({
        expanded: false,
        buttonText: props.placeholder || '',
        selectedId: '',
        selectedValue: props.value || '',
        selectedObject: undefined,
        height: '0px',
    });
    const [isFocused, setIsFocused] = useState(false);
    const [previousOnChangeValue, setPreviousOnChangeValue] = useState(state.selectedValue);
    const [optionElements, setOptionElements] = useState<HTMLDivElement[]>([]);
    const options = useMemo(() => {
        return props.children;
    }, [props.children]);
    const viewportIsTabletSized = useMediaQuery(`(max-width: ${responsiveBreakpointsPx.md}px)`);

    const selectRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const internalButtonRef = useRef<HTMLButtonElement>(null);
    const buttonRef = props.buttonRef ?? internalButtonRef;
    const optionsContainerRef = useRef<HTMLDivElement>(null);
    const initialValueHandled = useRef(false);
    const closeOnOutsideClick = useCallback(() => {
        setState((currentState) => ({ ...currentState, expanded: false, height: '0px' }));
    }, []);

    const { registerOption, selectActions } = useSelectContextUtils(
        optionElements,
        setOptionElements,
        state,
        setState,
        buttonRef.current,
        initialValueHandled,
    );
    useClickOutside(selectRef, CLICK_OUTSIDE_STATE, closeOnOutsideClick);

    useEffect(() => {
        if (!props.disabled && state.expanded) {
            const options = optionsContainerRef.current;

            if (options) {
                const currentHeight = options.style.height;
                options.style.height = 'auto';
                const newHeight = options.offsetHeight + 'px';
                options.style.height = currentHeight;

                setState({ ...state, height: newHeight });
            }
        }
        // `recalculateHeight` is triggered by consumer - other deps could cause this code to execute unexpectedly
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.recalculateHeight]);

    useEffect(() => {
        selectActions.selectOption({
            buttonText: props.placeholder || props.value || '',
            selectedId: state.selectedId ?? '',
            selectedValue: props.value || '',
            shouldFocus: false,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value, props.placeholder]);

    useEffect(() => {
        if (initialValueHandled.current) {
            if (
                props.onChange &&
                inputRef.current &&
                // Prevents `props.onChange(...)` spam before value stabilizes upon component creation:
                state.selectedValue !== previousOnChangeValue
            ) {
                setPreviousOnChangeValue(state.selectedValue);

                props.onChange(inputRef.current, {
                    value: state.selectedValue,
                    data: state.selectedObject,
                });
            }
        } else {
            initialValueHandled.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.selectedValue]);

    useBodyScrollLock(state.expanded && props.theme === 'grayLightWeb2' && viewportIsTabletSized);

    useEffect(() => {
        if (state.expanded && selectRef.current) {
            // Timeout allows for keyboard `Enter` keypress focus:
            const timer = setTimeout(() => {
                const selectedOption = optionElements.find((element) => element.dataset.value === state.selectedValue);
                selectedOption?.focus();
            }, 200);
            return () => {
                clearTimeout(timer);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.expanded]);

    function expandOptions() {
        if (!props.disabled) {
            const options = optionsContainerRef.current;

            if (options) {
                options.style.height = 'auto';
                options.style.visibility = 'visible';
                const height = options.offsetHeight + 'px';
                options.style.height = '';
                options.style.visibility = '';

                setTimeout(() => {
                    setState({ ...state, expanded: true, height: height });
                });
            }
        }
    }

    function closeOptions() {
        setState({ ...state, expanded: false, height: '0px' });
    }

    function toggleOptions() {
        if (state.expanded) {
            closeOptions();
        } else {
            expandOptions();
        }
    }

    function letterSearch(event: React.KeyboardEvent) {
        const lettersRegExp = new RegExp('^[a-zA-Z]?$');
        if (lettersRegExp.test(event.key)) {
            optionElements
                .find((option) => option.textContent?.toLowerCase().startsWith(event.key.toLowerCase()))
                ?.focus();
        }
    }

    function buttonKey(event: React.KeyboardEvent) {
        if (event.code === 'Enter' || event.code === 'Space') {
            event.preventDefault();
            toggleOptions();
        } else if (event.code === 'ArrowDown') {
            if (!state.expanded) {
                expandOptions();
            }
        } else if (event.code === 'Escape') {
            closeOptions();
        } else {
            letterSearch(event);
        }
    }
    function handleBlur(event: React.SyntheticEvent<HTMLElement>) {
        setIsFocused(false);
        if (props.onBlur && inputRef.current && !state.expanded) {
            props.onBlur({ ...event, currentTarget: inputRef.current });
        }
    }
    const selectId = useId();
    const labelId = `SelectLabel-${selectId}`;
    const buttonId = `SelectButton-${selectId}`;
    const listboxId = `SelectListbox-${selectId}`;
    const errorId = `${selectId}-error`;
    const type = props.type ? `type${ucfirst(props.type)}` : '';
    const theme = props.theme ? `theme${ucfirst(props.theme)}` : '';
    const isInvalid = props.invalid === VALIDITY.invalid;

    const selectClassNames = cn(
        styles.selectContainer,
        styles[type],
        styles[theme],
        styles.themeIfWeb2,
        props.className,
        {
            [styles.expanded]: state.expanded,
            [styles.hasValue]: state.selectedValue,
            [styles.invalid]: isInvalid,
            [styles.disabled]: props.disabled,
            [styles.isFocused]: isFocused,
        },
    );

    function renderSelectHeader() {
        const { selectHeader } = props;
        return (
            <div className={selectHeader?.body ? styles.selectPlanHeaderWrapper : styles.noPlanHeaderWrapperOnDesktop}>
                <div className={styles.planHeaderTitleWrapper}>
                    <div className={styles.selectPlanHeaderTitleStyles}>{selectHeader?.title}</div>
                    <button
                        className={styles.clickableArea}
                        onClick={toggleOptions}
                        aria-label={`close ${selectHeader?.title} modal`}
                    >
                        <CloseIcon />
                    </button>
                </div>
                {selectHeader?.body && <div id={`${listboxId}-description`}>{selectHeader.body}</div>}
            </div>
        );
    }

    const selectButtonWrapper = cn(styles.selectButton, {
        [styles.labelPosition]: state.selectedValue !== '',
    });

    const content = (
        <>
            {props.invalid && props.invalidMessage && props.required && (
                <InvalidFeedback validState={props.invalid} errorId={errorId} dataCy={props?.errorDataCy}>
                    {props.invalidMessage}
                </InvalidFeedback>
            )}
            <div ref={selectRef} className={selectClassNames} aria-disabled={props.disabled} data-cy={props?.dataCy}>
                <div className={selectButtonWrapper}>
                    <label htmlFor={buttonId} id={labelId}>
                        {props.label}
                        {props.required && <span className="show-for-sr">(Required)</span>}
                    </label>
                    <button
                        ref={buttonRef}
                        type="button"
                        role="combobox"
                        id={buttonId}
                        aria-controls={listboxId}
                        onBlur={handleBlur}
                        onFocus={() => setIsFocused(true)}
                        onMouseUp={toggleOptions}
                        onKeyDown={buttonKey}
                        aria-haspopup="listbox"
                        aria-expanded={state.expanded ? 'true' : 'false'}
                        aria-required={props.required || undefined}
                        aria-invalid={isInvalid || undefined}
                        aria-labelledby={!props.ariaLabel ? labelId + ' ' + buttonId : undefined}
                        aria-label={props.ariaLabel}
                        data-isvalid={!isInvalid}
                        aria-describedby={isInvalid ? errorId : undefined}
                        data-trackbutton
                        data-cy="select-button"
                    >
                        <span>{state.buttonText}</span>
                        <IconDropDown className={styles.iconDropDown} />
                    </button>
                    <input ref={inputRef} type="hidden" name={props.name} value={state.selectedValue} />
                </div>
                <div
                    ref={optionsContainerRef}
                    id={listboxId}
                    onKeyDown={letterSearch}
                    className={`${styles.selectOptions} ${state.expanded ? styles.expanded : ''}`}
                    style={{ height: state.height }}
                    aria-activedescendant={state.selectedId || undefined}
                    aria-hidden={state.expanded ? 'false' : 'true'}
                    role="listbox"
                    aria-labelledby={labelId}
                    aria-describedby={props.selectHeader?.body ? `${listboxId}-description` : undefined}
                    tabIndex={-1}
                >
                    <SelectContext.Provider value={{ registerOption, selectActions }}>
                        {props.selectHeader && <>{renderSelectHeader()}</>}
                        <div data-cy="select-options-container" className={styles.optionList}>
                            {options}
                        </div>
                    </SelectContext.Provider>
                </div>
                <div className={styles.overlay} role="presentation" onClick={() => toggleOptions()} />
            </div>
        </>
    );

    return props.selectWrapperStyles ? (
        <div className={props.selectWrapperStyles} id={props.wrapperId}>
            {content}
        </div>
    ) : (
        content
    );
}
